import { FolderPlusIcon, TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { query, where } from "firebase/firestore";
import { Alert, AlertTitle } from "components/ui/alert";
import {
  DialogHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { StyledTabs } from "components/StyledTabs";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { Button } from "components/button";
import { getProjectsCollection } from "integrations/firebase/collections";
import { useSession } from "contexts/AuthContext";
import { ProjectType } from "reedy-data/models";
import { Text } from "components/text";

export const AddToProjectModal = ({
  open,
  onOpenChange,
  onCreateProject,
  onSelectProject,
  selectedBooksCount,
}: {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCreateProject: () => void;
  onSelectProject: (projectId: string) => void;
  selectedBooksCount: number;
}) => {
  const { t } = useTranslation(["books"]);
  const [tab, setTab] = useState<"user" | "organisation">("user");
  const { organisation, user } = useSession();

  const [searchQuery, setSearchQuery] = useState("");

  const [projects = []] = useCollectionData(
    query(
      getProjectsCollection(),
      where("organisation.id", "==", organisation.id),
      where("type", "==", ProjectType.ISBNS),
    ),
  );

  const filteredProjects = projects
    .filter((project) => (tab === "user" ? project.userId === user.id : true))
    .filter((project) =>
      project.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => a.name?.localeCompare(b.name || "") || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[40vw] max-w-[40vw] h-[40vh] flex flex-col p-0">
        <DialogHeader className="items-start space-y-0 my-0 flex-shrink-0 overflow-hidden">
          <DialogTitle className="sr-only">
            {t("books:addToProject.modal.title")}
          </DialogTitle>
          <DialogDescription hidden className="sr-only">
            {t("books:addToProject.modal.description")}
          </DialogDescription>
          <div className="px-4 pt-4 w-full">
            <div className="flex">
              <Badge
                variant="secondary"
                className="min-w-2 px-2 py-1 block font-normal"
              >
                {t("books:addToProject.selected", {
                  count: selectedBooksCount,
                })}
              </Badge>
            </div>

            <div className="px-1 flex w-full">
              <input
                type="text"
                className="w-full py-2 mt-4 text-muted-foreground outline-0"
                placeholder={t("books:addToProject.search.placeholder")}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          </div>

          <Separator />

          <div className="px-4 py-1 flex">
            <StyledTabs
              activeTab={tab}
              tabs={[
                { value: "user", label: t("books:addToProject.tabs.user") },
                {
                  value: "organisation",
                  label: t("books:addToProject.tabs.organisation"),
                },
              ]}
              onTabChange={(value) => setTab(value)}
              className="flex flex-col flex-1 min-h-0 h-9"
              listClassName="h-9"
              triggerClassName="px-3 py-1"
            />
          </div>
          {searchQuery ? (
            <Text as="div" variant="labelSmall" className="flex-1 px-5">
              {t("books:addToProject.search.label", { query: searchQuery })}
            </Text>
          ) : null}
        </DialogHeader>

        <div className="flex-1 px-4 overflow-y-auto h-24">
          {filteredProjects.length === 0 ? (
            <Alert className="bg-system-warning/10 border-system-warning/20">
              <TriangleAlertIcon className="h-4 w-4" />
              <AlertTitle>{t("books:addToProject.noResults")}</AlertTitle>
            </Alert>
          ) : (
            <div>
              {filteredProjects.map((project) => (
                <button
                  type="button"
                  key={project.id}
                  className="w-full hover:bg-accent [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                  onClick={() => onSelectProject(project.id!)}
                >
                  {project.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex px-2">
          <div>
            <Separator />
          </div>
          <div className="flex w-full">
            <Button
              variant="ghost"
              className="mb-2 w-full justify-start"
              onClick={onCreateProject}
            >
              <FolderPlusIcon className="h-4 w-4 mr-1" />
              {t("books:addToProject.button.createProject")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
